import '../searchinput/ui-searchinput';
import '../modal/ui-modal-controller';
import '../option/ui-option-basic';
import '../option/ui-option-account';
import '../option/ui-option-multipayment';
import { UIModal } from '../modal/ui-modal';
import { UIElement } from '../ui-element';
import {
    debounce,
    appendModalControllerIfRequired,
    makePopoverHandler,
    resolveVerticalPopoverPlacement,
} from '../../global/helpers';
import { isKeyPressed, keyCodes, TabIndex } from '../../global/keyboard';
import {
    closestByCond,
    dispatchNativeEvent,
    isParentOf,
    getBaseGridPoint,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { Labels } from '../../global/labels';
import { Alignment } from '../../global/enums';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    EventHandler,
    HTMLElementOrParent,
    NullOr,
    UndefinedOr,
} from '../../global/utils.type';
import type {
    Attributes,
    AttributesConfig,
    ElementConfig,
} from '../ui-element.type';
import type { UISearchInput } from '../searchinput/ui-searchinput';
import type { UIDatalist } from '../datalist/ui-datalist';
import { isAccountTypeLayout } from './ui-autocomplete.helpers';
import {
    UIAutocompleteOptionConstructor,
    UIAutocompleteOptionElement,
} from './ui-autocomplete.type';
import styles from './ui-autocomplete.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAutocomplete
 * @element ui-autocomplete
 * @classdesc Represents a class for <code>ui-autocomplete</code> element.
 * After typing, it suggests an option to choose for auto-completion.
 * @fires event:search
 * @fires event:pick
 * @listens onkeydown
 * @listens event:clear
 * Set id on the slotted input to use it as the base for the control and for hint/options/assistant
 * ids (e.g. autocomplete-hint-{id}). If no id is set, then one is generated automatically.
 * @property {"account" | "basic" | "multipayment" | "search"} [layout="basic"]
 *  {@attr layout}
 * Layout for options inside
 *  {@desc account}
 *  {@desc basic}
 *  {@desc multipayment}
 *  {@desc search}
 * @property {number} [maxlength] {@attr maxlength} Sets how many options should be shown in
 * popover, the rest will be scrolled.
 * @property {number} [minmatch=-1] {@attr minmatch} This determines minimum number
 * of matching characters
 * @property {number} [debounce=0] {@attr debounce} Debounce time to requests.
 * @property {number} [external] {@attr external} Make the component controllable by external data
 * provider.
 * @property {("basic")} [type] {@attr type} Deprecated: Type of the autocomplete popover.
 * @property {string} value Value of the field
 * @property {string} option Tag name of the option
 * @property {boolean} touched True if user touched the autocomplete.
 * @property {string} uuid Unique identifier of the autocomplete input.
 * @property {HTMLInputElement} control
 * @property {UISearchInput} search Shortcut to UISearchInput child element.
 * @property {Function} comparator Comparator function to find matches.
 * @property {UIDatalist} datalist Shortcut to UIDatalist child element.
 * @property {HTMLUListElement} options Shortcut to list of entries.
 * @property {HTMLDivElement} popoverElement Shortcut to a popover element.
 * @property {HTMLDivElement} assistant Shortcut to an assistant element.
 * @property {HTMLDivElement} hint Shortcut to hint element (removed after input).
 * @slot
 * @example
 * <ui-field>
 *   <ui-autocomplete>
 *     <input type="search" id="custom-id">
 *     <ui-datalist>
 *       <option value="value" data-whatever="whatever">
 *       <option value="somevalue" data-whatever="somedata">
 *     </ui-datalist>
 *   </ui-autocomplete>
 * </ui-field>
 */
@Component({
    tag: 'ui-autocomplete',
    styles,
})
export class UIAutocomplete extends UIElement {
    /**
     * @type {Record<string, string>}
     */
    static readonly Layout = {
        Account: 'account',
        Basic: 'basic',
        Multipayment: 'multipayment',
        Search: 'search',
    } as const;

    @Attribute(String) layout: string = UIAutocomplete.Layout.Basic;
    @Attribute(Number) minmatch: number = -1;
    @Attribute(Number) maxlength?: number;
    @Attribute(Boolean) external?: boolean;
    @Attribute(Number) debounce: number = 0;

    @Child('ui-searchinput') search!: UISearchInput;
    @Child('.ui-autocomplete__control') control!: HTMLInputElement;
    @Child('ui-datalist') datalist!: UIDatalist;
    @Child('.ui-autocomplete__assistant') assistant!: HTMLDivElement;
    @Child('.ui-autocomplete__hint') hint!: HTMLDivElement;

    touched = false;

    private _detachedPopover: NullOr<HTMLDivElement> = null;
    private _outsideMousedownHandler: NullOr<EventHandler> = null;
    private _prevValue?: string;
    private _preventSearch = false;
    private _activeOptionIndex = -1;
    private _uuid?: string;
    private observer!: MutationObserver;
    private inputChangeHandler!: EventListener;
    private inputFocusHandler!: EventListener;
    private keyboardHandler!: EventListener;
    private clearHandler!: EventListener;

    /**
     * @deprecated
     * @type {Record<string, string>}
     */
    static readonly Type = {
        Basic: 'basic',
    } as const;

    /**
     * @type {MutationObserverInit}
     * @private
     */
    private static readonly ObserveOptions: MutationObserverInit = {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
    };

    /**
     * Provides getter and setter for "value" property
     * and link it to the "value" attribute
     * syncs with inner input
     */
    get value(): string {
        return this.control.value;
    }

    set value(value: string) {
        const input = this.control;
        if (input.value === value) {
            return;
        }
        setNativeInputValue(input, value);
    }

    get option(): `${string}-${string}` {
        return `ui-option-${this.layout}`;
    }

    set option(value: string) {
        if (!value?.startsWith('ui-option-')) {
            this.layout = '';
        }
        this.layout = value?.replace('ui-option-', '') ?? '';
    }

    get options(): NullOr<HTMLUListElement> {
        return this.popoverElement?.querySelector('.ui-autocomplete__options');
    }

    get popoverElement(): HTMLDivElement {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector('.ui-autocomplete__popover')!;
    }

    /**
     * @deprecated
     * @type {string}
     */
    get type(): NullOr<string> {
        if (this.layout === UIAutocomplete.Layout.Search) {
            return this.layout;
        }
        const type = this.getAttribute('type');
        if (
            [
                UIAutocomplete.Type.Basic,
                (UIAutocomplete.Type as Attributes).Plain,
            ].includes(type)
        ) {
            return type;
        }
        this.removeAttribute('type');
        const elementFactory = customElements.get(
            this.option
        ) as UndefinedOr<UIAutocompleteOptionConstructor>;
        if (!elementFactory) {
            return null;
        }
        return elementFactory.PopoverType ?? null;
    }

    set type(_value: NullOr<string>) {
        // do nothing
    }

    /**
     * Gets and sets a comparator function to find matches.
     */
    get comparator(): (
        option: HTMLOptionElement,
        inputText: string
    ) => boolean {
        const el = customElements.get(
            this.option
        ) as UndefinedOr<UIAutocompleteOptionConstructor>;
        return el && typeof el.prototype?.findMatch === 'function'
            ? el.prototype.findMatch
            : () => true;
    }

    set comparator(
        _value: (option: HTMLOptionElement, inputText: string) => boolean
    ) {
        // do nothing
    }

    get uuid(): string {
        this._uuid ??= Digest.randomId();
        return this._uuid;
    }

    /**
     * Returns the control ID for ui-field (label[for], aria-describedby).
     * Priority: user id on input > generated. Always returns a string so ui-field can set label[for].
     * @returns {string}
     */
    getControlIdForField(): string {
        const input = this.querySelector('input');
        if (input) {
            let id = input.getAttribute('id') || input.id;
            if (!id) {
                id = this.uuid;
                input.id = id;
                input.setAttribute('id', id);
            }
            return id;
        }
        return this.uuid;
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-autocomplete', {
            hint: 'Use up and down arrows to review and enter to select.',
            suggestions: 'Suggestions',
            suggestionsFound: 'Suggestions displayed: ',
            noSuggestionsFound: 'No suggestions displayed',
        });
    }

    /**
     *  Returns the list of options.
     *  @param {number} [index] If index is set return options with index.
     *  @returns {Array<HTMLLIElement> | HTMLLIElement | undefined}
     */
    getOptions(): Array<HTMLLIElement>;
    getOptions(index: -1): Array<HTMLLIElement>;
    getOptions(index: number): UndefinedOr<HTMLLIElement>;
    getOptions(index = -1): Array<HTMLLIElement> | UndefinedOr<HTMLLIElement> {
        const optionEls = Array.from(
            this.popoverElement?.querySelectorAll<HTMLLIElement>(
                '.ui-autocomplete__option'
            ) ?? []
        );
        return index === -1 ? optionEls : optionEls[index];
    }

    /**
     * Checks if the value in the input is valid based on the minmatch attribute.
     * @returns {boolean}
     */
    isValueValid(): boolean {
        const value = this.control.value;
        if (!(this.minmatch > -1)) {
            return true;
        }
        return !!(value && value.length >= this.minmatch);
    }

    /**
     * Get popover height in pixels.
     * @returns {number}
     */
    getPopoverHeight(): number {
        const options = this.popoverElement?.querySelectorAll<HTMLElement>(
            '.ui-autocomplete__option, .ui-autocomplete__group > [optgroup]'
        );
        if (!options?.length) {
            this.closePopover();
            return 0;
        }

        const limit = this.maxlength
            ? Math.min(this.maxlength, options.length)
            : options.length;

        const lastVisibleItem = options[limit - 1];
        return lastVisibleItem.offsetTop + lastVisibleItem.clientHeight;
    }

    /**
     * Sets popover height in pixels.
     * @param {number} value
     */
    setPopoverHeight(value: number): void {
        /** @type {HTMLElement} */
        const options = this.popoverElement?.querySelector<HTMLElement>(
            '.ui-autocomplete__options'
        );
        if (!options) {
            return;
        }
        const newHeight = value > 0 ? `${value}px` : null;
        const oldHeight = options.style.maxHeight;

        if (oldHeight === newHeight) {
            return;
        }

        options.style.maxHeight = String(newHeight);
    }

    /**
     * Sets popover width in pixels.
     * @param {number} value
     */
    setPopoverWidth(value: number): void {
        if (!this.popoverElement) {
            return;
        }
        if (value > 0) {
            this.popoverElement.style.width = `${value}px`;
        }
    }

    /**
     * Checks if autocomplete has options.
     * @returns {boolean}
     */
    hasOptions(): boolean {
        return !!this.popoverElement?.querySelector('.ui-autocomplete__option');
    }

    /**
     * Checks if the popover is visible.
     * @returns {boolean}
     */
    isPopoverVisible(): boolean {
        return !!this._detachedPopover;
    }

    /**
     * Checks if the event comes from a modal which no longer holds the current popover.
     * Modals close asynchronously, so a modal from a previous open cycle can emit
     * "modal-close" after a new popover has already been opened.
     * @param {Event} [event]
     * @returns {boolean}
     */
    isStalePopoverModalEvent(event?: Event): boolean {
        const closingModal = (
            event?.target as NullOr<HTMLElement>
        )?.closest?.<UIModal>('ui-modal');
        if (!closingModal) {
            return false;
        }
        return closingModal !== this._detachedPopover?.closest('ui-modal');
    }

    /**
     * Opens detached popover.
     * @fires event:modal-open
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }
        const getPlacement = () =>
            resolveVerticalPopoverPlacement({
                triggerRectYAxis: this.control.getBoundingClientRect(),
                fullPopoverHeight: this.getPopoverHeight(),
                viewportEdgeOffset: getBaseGridPoint() * 3,
                // Search layout is pinned to open downward only
                ...(this.layout === UIAutocomplete.Layout.Search
                    ? { forcedSide: Alignment.Bottom }
                    : {}),
            });
        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type:
                this.layout === UIAutocomplete.Layout.Search
                    ? UIModal.Type.Popover
                    : UIModal.Type.Menu,
            content: this.popoverElement,
            params: {
                disableKeyboardTrapping: true,
                attributes: {
                    'no-bottom-sheet': true,
                    'compact': true,
                },
                classList: {
                    '-autocomplete': true,
                    '-search': this.layout === UIAutocomplete.Layout.Search,
                },
                position: makePopoverHandler(this, {
                    supportMiddleAlignment: false,
                    supportTopAlignment: false,
                    supportBottomAlignment: false,
                    verticalAxisSide: () => getPlacement().verticalAxisSide,
                    topOffset: () => {
                        const hasScrollbar =
                            this.options &&
                            this.options.scrollHeight >
                                this.options.clientHeight;
                        return hasScrollbar ? getBaseGridPoint() : 0;
                    },
                    popoverHeight: () => {
                        this.setPopoverHeight(getPlacement().popoverHeight);
                        this.setPopoverWidth(this.offsetWidth);
                        return this.popoverElement?.offsetHeight || 0;
                    },
                }),
                target: this,
                onClose: (event?: Event) => {
                    if (this.isStalePopoverModalEvent(event)) {
                        return;
                    }
                    this.closePopover();
                },
                onOpen: () => this.control.focus(),
            },
        });
        this.classList.add('-active');
    }

    /**
     * Closes detached popover.
     */
    closeDetachedPopover(): void {
        if (!this._detachedPopover) {
            return;
        }
        const modal = this._detachedPopover.closest<UIModal>('ui-modal');
        if (modal) {
            this._detachedPopover = null;
            modal.close();
        }
        this.classList.remove('-active');
    }

    /**
     * Opens dropdown popup.
     */
    openPopover(): void {
        if (this.isPopoverVisible() || this.control?.hasAttribute('readonly')) {
            return;
        }
        this.popoverElement?.offsetWidth.toString();
        this.options?.addEventListener('click', this.handleClickOnOption);
        this.setPopoverHeight(this.getPopoverHeight());
        this.openDetachedPopover();

        // Uses mousedown instead of click because when the popover causes page scroll,
        // the browser retargets the click event to <body>, triggering a false outside-click close.
        // Caused by the fact that autocomplete's popover top alignment broken.
        this._outsideMousedownHandler = (event: Event) => {
            if (!this.isInnerElement(event.target as HTMLElement)) {
                this.closePopover();
            }
        };
        document.addEventListener('mousedown', this._outsideMousedownHandler);
    }

    /**
     * Closes dropdown popup.
     */
    closePopover(): void {
        this.control?.setAttribute('aria-expanded', 'false');
        this.removeAttribute('data-popover-align');
        this.control?.removeAttribute('aria-activedescendant');
        if (!this.isPopoverVisible()) {
            return;
        }
        this.options?.removeEventListener('click', this.handleClickOnOption);
        if (this._detachedPopover) {
            this.control?.focus();
            this.closeDetachedPopover();
        }
        if (this._outsideMousedownHandler) {
            document.removeEventListener(
                'mousedown',
                this._outsideMousedownHandler
            );
            this._outsideMousedownHandler = null;
        }
        this.clearOptionHighlight();
        if (this.assistant) {
            this.assistant.textContent = '';
        }
    }

    /**
     * Updates content of popover.
     */
    syncOptions(): void {
        if (this.options) {
            this.rebuildChildren.call(this.options, this.buildOptions());
            this.setPopoverHeight(this.getPopoverHeight());
            this.clearOptionHighlight();
        }
    }

    /**
     * Updates content of popover.
     * @fires event:popover-updated
     */
    updatePopover(): void {
        this.syncOptions();
        this.setPopoverHeight(this.getPopoverHeight());
        if (
            document.activeElement === this.control &&
            // && this.control.value
            this._prevValue !== this.control.value &&
            this.hasOptions()
        ) {
            if (!this.isPopoverVisible()) {
                this.openPopover();
            } else {
                this.dispatchCustomEvent('popover-updated');
            }
        }
    }

    /**
     * Renders wrapper for one option group (optgroup).
     * @param {HTMLOptGroupElement} group
     * @param {number} groupIndex - Index of this group in the top-level options list; used for unique option ids.
     * @returns {ElementConfig}
     */
    renderOptionGroup(
        group: HTMLOptGroupElement,
        groupIndex: number
    ): ElementConfig {
        let children = [...group.children] as Array<HTMLOptionElement>;
        if (!this.external) {
            children = this.filterOptionsBasedOnInput(children);
        }
        return {
            tagName: 'li',
            classList: {
                'ui-autocomplete__group': true,
                // hide zero options ?
                // '-hidden': this.hideSelected && option.selected
            },
            children: [
                this.renderOption(group),
                {
                    tagName: 'ul',
                    classList: {
                        'ui-autocomplete__group-options': true,
                    },
                    attributes: {
                        'role': 'group',
                        'aria-label': group.label || null,
                    },
                    children: children.map((node, index) =>
                        this.renderOptionWrapper(
                            node,
                            index,
                            children.length,
                            groupIndex
                        )
                    ),
                },
            ],
        };
    }

    /**
     * Renders wrapper for a single option.
     * @param {HTMLOptionElement} option
     * @param {number} index
     * @param {number} total
     * @param {number} [groupIndex] - When inside an optgroup, pass group index so option ids are unique across groups.
     * @returns {object}
     */
    renderOptionWrapper(
        option: HTMLOptionElement,
        index: number,
        total: NullOr<number> = null,
        groupIndex: UndefinedOr<number> = undefined
    ): ElementConfig {
        const baseId =
            this.control?.getAttribute('id') || this.control?.id || this.uuid;
        const optionId =
            option.id ||
            (groupIndex !== undefined
                ? `${baseId}-option-${groupIndex}-${index}`
                : `${baseId}-option-${index}`);
        return {
            tagName: 'li',
            attributes: {
                'key': option.getAttribute('data-key') || null,
                'role': 'option',
                // These two attributes are only needed when total number of options is not rendered to DOM. Without them, screen readers still announce position and total properly, as long as all options are added to DOM.
                // We keep them for compatibility with future possible support for virtual scrolling.
                // Note also aria-posinset is not calculated correctly when optgroups are present. This is not a problem in NVDA which doesn't rely on it, but VO/Mac, while not supporting counting in nested lists as groups, does rely on them when they are present.
                'aria-setsize': total !== null ? String(total) : null,
                'aria-posinset': total !== null ? index + 1 : null,
                'aria-label': !isAccountTypeLayout(this.layout)
                    ? option.value || option.innerText
                    : '',
                'aria-selected': 'false',
                'id': optionId,
            },
            classList: {
                'ui-autocomplete__option': true,
            },
            events: {
                keydown: (event: UndefinedOr<Event | CustomEvent>) => {
                    if (!(event instanceof KeyboardEvent)) {
                        return;
                    }
                    if (
                        isKeyPressed(event, keyCodes.SPACE) ||
                        isKeyPressed(event, keyCodes.ENTER)
                    ) {
                        event.preventDefault();
                        (event.target as HTMLElement)?.click();
                    }
                },
            },
            children: [this.renderOption(option)],
        };
    }

    /**
     * Renders one option.
     * @param {HTMLOptionElement | HTMLOptGroupElement} option
     * @returns {ElementConfig}
     */
    renderOption(
        option: HTMLOptionElement | HTMLOptGroupElement
    ): ElementConfig {
        const isGroup = option instanceof HTMLOptGroupElement;
        const additionalAttributes: AttributesConfig = {
            'aria-hidden': !isAccountTypeLayout(this.layout),
        };

        if (isGroup) {
            return {
                tagName: this.option,
                attributes: {
                    ...additionalAttributes,
                    optgroup: true,
                    text: option.getAttribute('label'),
                },
            };
        }

        const inputText = this.control.value;
        const origValue = option.value || '';
        if (origValue.trim() && inputText.trim()) {
            const reg = new RegExp(inputText, 'gi');
            additionalAttributes.formatted = origValue
                .replace(/\*/g, '')
                .replace(reg, '**$&**');
        }

        return {
            tagName: this.option,
            attributes: {
                ...additionalAttributes,
                ...this.datalist.getOptionAttributes(option, false),
            },
        };
    }

    /**
     * Builds options/entries for autocomplete.
     * @returns {Array<ElementConfig>}
     */
    buildOptions(): Array<ElementConfig> {
        let options: Array<HTMLOptionElement | HTMLOptGroupElement> = this
            .datalist
            ? [].slice.call(this.datalist.children, 0)
            : [];
        const inputText = this.control.value;
        const isValid = this.isValueValid();
        if (!this.external) {
            const matchFn = (
                option: HTMLOptionElement | HTMLOptGroupElement
            ): boolean => {
                if (option instanceof HTMLOptGroupElement) {
                    return [].some.call(option.children, matchFn);
                }
                if (!isValid) {
                    return false;
                }
                return inputText ? this.comparator(option, inputText) : true;
            };
            options = [].filter.call(options, matchFn);
        }
        this.control.setAttribute('aria-expanded', String(options.length > 0));
        const total = this.getTotalOptions(options);
        if (this.debounce) {
            setTimeout(() => this.setAssistiveFoundText(total), this.debounce);
        } else {
            this.setAssistiveFoundText(total);
        }
        return options.map((optionItem, index) => {
            if (optionItem instanceof HTMLOptGroupElement) {
                return this.renderOptionGroup(optionItem, index);
            }
            return this.renderOptionWrapper(optionItem, index, options.length);
        });
    }

    /**
     * Filter option elements based on input value
     * @param {Array<HTMLOptionElement> | HTMLCollection} options
     * @returns {Array<HTMLOptionElement>}
     */
    filterOptionsBasedOnInput(
        options: ArrayLike<HTMLOptionElement>
    ): Array<HTMLOptionElement> {
        const searchQuery = this.control.value;
        return Array.from(options).filter(
            (option) =>
                !searchQuery.trim() || this.comparator(option, searchQuery)
        );
    }

    /**
     * Get total number of filtered options, including ones within optgroups.
     * @param {Array<HTMLOptionElement | HTMLOptGroupElement>} options
     * @returns {number}
     */
    getTotalOptions(
        options: Array<HTMLOptionElement | HTMLOptGroupElement>
    ): number {
        let count = 0;
        options.forEach((option) => {
            if (option instanceof HTMLOptGroupElement) {
                const children = this.filterOptionsBasedOnInput([
                    ...option.children,
                ] as Array<HTMLOptionElement>);
                count += children.length;
            } else {
                count += 1;
            }
        });
        return count;
    }

    /**
     * Sets assistive text how many records found.
     * @param {number} count
     */
    setAssistiveFoundText(count: number): void {
        if (!this.assistant) {
            return;
        }
        if (!this.touched) {
            this.assistant.textContent = '';
        } else {
            if (count > 0) {
                this.assistant.textContent =
                    String(UIAutocomplete.labels.suggestionsFound) + count;
            } else {
                this.assistant.textContent = String(
                    UIAutocomplete.labels.noSuggestionsFound
                );
            }
            setTimeout(() => {
                this.assistant.textContent = '';
            }, 4000);
        }
    }

    /**
     * Fires callback when the input got a focus state.
     * @param {Event} _event
     * @private
     */
    private handleInputFocus(_event?: Event): void {
        this.touched = true;
        if (this._preventSearch) {
            this._preventSearch = false;
            return;
        }
        if (this.isPopoverVisible()) {
            return;
        }
        this.syncOptions();
        if (this.popoverElement?.querySelector('.ui-autocomplete__option')) {
            this.openPopover();
        }
        this.handleSearchRequest(this.control.value, this.control.name);
    }

    /**
     * Fires callback when the input's value is updated.
     * @private
     */
    private handleInputChange(): void {
        if (!this.isValueValid()) {
            this.closePopover();
            return;
        }
        this.updatePopover();
        const popover = this.popoverElement;

        /* if matches are empty don't show empty popover */
        if (popover?.querySelector('.ui-autocomplete__option')) {
            this.openPopover();
        } else {
            this.closePopover();
        }

        this.handleSearchRequest(
            this.control.value,
            this.control.getAttribute('name')
        );
        // remove after any input.
        if (this.hint) {
            this.hint.remove();
            this.removeAttribute('aria-describedby');
        }
    }

    /**
     * Fires callback when debounced search request should be called.
     * @param {string} value
     * @param {string} [key]
     * @private
     */
    private handleSearchRequest(value: string, key?: NullOr<string>): void {
        this.dispatchCustomEvent('search', { value: value || '', key: key });
    }

    /**
     * Fires callback when user clicked on option.
     * @param {MouseEvent} event
     * @private
     */
    private handleClickOnOption(event: MouseEvent): void {
        const option = closestByCond(
            event.target as HTMLElementOrParent,
            (node: HTMLElementOrParent) => {
                return (
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node as HTMLElement).classList.contains(
                        'ui-autocomplete__option'
                    )
                );
            }
        ) as NullOr<HTMLLIElement>;
        this.pickOption(option);
    }

    /**
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isInnerElement(element: HTMLElement): boolean {
        if (this.isParentOf(element)) {
            return true;
        }
        return !!(
            this._detachedPopover && isParentOf(this._detachedPopover, element)
        );
    }

    /**
     * Fires callback when user clears input.
     * @private
     */
    private handleClear(): void {
        if (this.isPopoverVisible()) {
            this.closePopover();
        }
    }

    /**
     * Highlights the option at the given index.
     * @param {number} index
     */
    highlightOption(index: number): void {
        const options = this.getOptions();
        options.forEach((o: HTMLLIElement, i: number) => {
            if (i === index) {
                o.classList.add('-active');
                o.setAttribute('aria-selected', 'true');
                this.control?.setAttribute('aria-activedescendant', o.id);
            } else {
                o.classList.remove('-active');
                o.setAttribute('aria-selected', 'false');
            }
        });
        this._activeOptionIndex = index;
    }

    /**
     * Clears highlight from all options.
     */
    clearOptionHighlight(): void {
        const options = this.getOptions();
        options.forEach((o: HTMLLIElement) => {
            o.classList.remove('-active');
            o.setAttribute('aria-selected', 'false');
        });
        this.control?.removeAttribute('aria-activedescendant');
        this._activeOptionIndex = -1;
    }

    /**
     * Handles keyboard events when input is in focus.
     * @param {KeyboardEvent} event
     * @private
     */
    private handleKeyboard(event: KeyboardEvent): void {
        const options = this.getOptions();
        if (!options.length) {
            return;
        }

        if (
            isKeyPressed(event, keyCodes.UP) ||
            isKeyPressed(event, keyCodes.DOWN)
        ) {
            if (document.activeElement !== this.control) {
                return;
            }

            event.preventDefault();

            const isOpen = this.isPopoverVisible();
            if (!isOpen && isKeyPressed(event, keyCodes.DOWN)) {
                this.openPopover();
            }

            const step = isKeyPressed(event, keyCodes.UP) ? -1 : 1;
            let nextIndex = this._activeOptionIndex + step;

            if (nextIndex < 0) {
                nextIndex = options.length - 1;
            }
            if (nextIndex >= options.length) {
                nextIndex = 0;
            }

            this.highlightOption(nextIndex);
        } else if (isKeyPressed(event, keyCodes.ENTER)) {
            if (
                document.activeElement === this.control &&
                this._activeOptionIndex > -1
            ) {
                event.preventDefault();
                const option = options[this._activeOptionIndex];
                this.pickOption(option);
            } else if (this.isPopoverVisible()) {
                event.preventDefault();
                this.closePopover();
            }
        } else if (
            isKeyPressed(event, keyCodes.ESCAPE) ||
            isKeyPressed(event, keyCodes.TAB)
        ) {
            this.closePopover();
        }
    }

    /**
     * Pick an option.
     * @param {HTMLLIElement} option
     * @private
     */
    private pickOption(option: NullOr<HTMLLIElement>): void {
        const uiOptionElement = option?.querySelector(
            '.ui-autocomplete__option > *'
        ) as NullOr<UIAutocompleteOptionElement>;
        if (!option || !uiOptionElement) {
            return;
        }
        const newValue =
            typeof uiOptionElement.getText === 'function'
                ? uiOptionElement.getText()
                : uiOptionElement.getAttribute('value');
        setNativeInputValue(this.control, newValue ?? '');
        this._prevValue = this.control.value;
        dispatchNativeEvent(this.control, 'input', true);
        dispatchNativeEvent(this.control, 'change', true);
        this._preventSearch = false;
        this.control?.focus();
        this.search?.updateStatus();
        this.closePopover();
        this.dispatchCustomEvent('pick', {
            option,
            value: this.control.value,
            data:
                typeof uiOptionElement.getData === 'function'
                    ? uiOptionElement.getData()
                    : {},
        });
    }

    /**
     * Fires callback when datalist got mutations.
     * @private
     */
    private handleDatalistMutations(): void {
        this.updatePopover();
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.control.addEventListener('input', this.inputChangeHandler);
        this.control.addEventListener('focus', this.inputFocusHandler);
        this.control.addEventListener('keydown', this.keyboardHandler);
        this.addEventListener('clear', this.clearHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.control.removeEventListener('input', this.inputChangeHandler);
        this.control.removeEventListener('focus', this.inputFocusHandler);
        this.control.removeEventListener('keydown', this.keyboardHandler);
        this.removeEventListener('clear', this.clearHandler);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.touched = false;
        // Resolve control and its id first so we can use that id as base for hint/options/assist.
        // Prefer a child input if present. If not, and we're inside a ui-field and adopt the preceding native input
        let control = this.queryChildren('input')[0] as HTMLInputElement;
        const datalist = this.queryChildren('ui-datalist')[0] as UIDatalist;
        const field = this.closest('ui-field');

        if (!control && field) {
            const prev = this.previousElementSibling;
            if (prev?.localName === 'input') {
                control = prev as HTMLInputElement;
            }
        }

        const inputId = control?.getAttribute('id') || control?.id || this.uuid;

        const baseId = inputId;
        const hintId = `autocomplete-hint-${baseId}`;
        const optionsId = `autocomplete-options-${baseId}`;
        const assistId = `autocomplete-live-${baseId}`;
        this.detachChildNodes();
        this.insertElements([
            {
                tagName: 'ui-searchinput',
                attributes: {
                    cleanable: true,
                    searchable: true,
                },
                children: [
                    {
                        tagName: 'input',
                        element: control,
                        attributes: {
                            'id': inputId,
                            'type': 'search',
                            'role': 'combobox',
                            'autocomplete': 'off',
                            'aria-autocomplete': 'list',
                            'aria-expanded': 'false',
                            'aria-describedby': hintId,
                            'aria-controls': optionsId,
                            'tabindex': control?.hasAttribute('readonly')
                                ? TabIndex.Inactive
                                : null,
                        },
                        classList: {
                            'ui-autocomplete__control': true,
                        },
                    },
                ],
            },
            {
                tagName: 'ui-datalist',
                element: datalist,
            },
            {
                tagName: 'div',
                classList: {
                    'ui-autocomplete__popover': true,
                    [`-layout-${this.layout}`]:
                        this.layout === UIAutocomplete.Layout.Search,
                },
                children: [
                    {
                        tagName: 'ul',
                        attributes: {
                            'id': optionsId,
                            'role': 'listbox',
                            'aria-label': UIAutocomplete.labels.suggestions,
                        },
                        classList: {
                            'ui-autocomplete__options': true,
                        },
                    },
                ],
            },
            {
                tagName: 'div',
                attributes: {
                    'id': assistId,
                    'aria-live': 'assertive',
                },
                classList: {
                    'ui-autocomplete__assistant': true,
                    '-visually-hidden': true,
                },
            },
            {
                tagName: 'div',
                attributes: {
                    'id': hintId,
                    'aria-live': 'polite',
                },
                classList: {
                    'ui-autocomplete__hint': true,
                    '-hidden': true,
                },
                children: [UIAutocomplete.labels.hint],
            },
        ] satisfies Array<ElementConfig>);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        appendModalControllerIfRequired();
        const boundInputChange = this.handleInputChange.bind(this);
        this.inputChangeHandler = this.debounce
            ? debounce(boundInputChange, this.debounce, true)
            : boundInputChange;
        this.inputFocusHandler = this.handleInputFocus.bind(this);
        this.keyboardHandler = (event: Event) => {
            if (event instanceof KeyboardEvent) {
                this.handleKeyboard(event);
            }
        };
        this.clearHandler = this.handleClear.bind(this);
        this.handleClickOnOption = this.handleClickOnOption.bind(this);
        this.handleSearchRequest = this.handleSearchRequest.bind(this);
        this.closePopover = this.closePopover.bind(this);

        this.subscribeEvents();
        this.observer = new MutationObserver(
            this.handleDatalistMutations.bind(this)
        );
        if (this.datalist) {
            this.observer.observe(this.datalist, UIAutocomplete.ObserveOptions);
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.closeDetachedPopover();
        if (this._outsideMousedownHandler) {
            document.removeEventListener(
                'mousedown',
                this._outsideMousedownHandler
            );
            this._outsideMousedownHandler = null;
        }
        this.observer.disconnect();
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        if (this.datalist) {
            this.observer.observe(this.datalist, UIAutocomplete.ObserveOptions);
        }
        this.subscribeEvents();
    }
}
